/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	GetLockoutBanner400,
	GetLockoutBanner403,
	GetLockoutBanner500,
	LockoutBanners,
	LockoutBannersCreate,
	PutLockoutBanner400,
	PutLockoutBanner404,
	PutLockoutBanner409,
	PutLockoutBanner500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getGetLockoutBannerUrl = (organizationVerdiId: string) => {
	return `/lockout-banners/${organizationVerdiId}`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Get an organization's lockout banner
 */
export const getLockoutBanner = async (
	organizationVerdiId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<LockoutBanners | null> => {
	return customFetch<LockoutBanners | null>(
		getGetLockoutBannerUrl(organizationVerdiId),
		{
			...options,
			method: "GET",
		},
	);
};

export const getGetLockoutBannerQueryKey = (organizationVerdiId: string) => {
	return ["lockout-banners", organizationVerdiId] as const;
};

export const getGetLockoutBannerQueryOptions = <
	TData = Awaited<ReturnType<typeof getLockoutBanner>>,
	TError =
		| GetLockoutBanner400
		| void
		| GetLockoutBanner403
		| GetLockoutBanner500,
>(
	organizationVerdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getLockoutBanner>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getGetLockoutBannerQueryKey(organizationVerdiId);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getLockoutBanner>>
	> = ({ signal }) =>
		getLockoutBanner(organizationVerdiId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: organizationVerdiId !== null && organizationVerdiId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getLockoutBanner>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLockoutBannerQueryResult = NonNullable<
	Awaited<ReturnType<typeof getLockoutBanner>>
>;
export type GetLockoutBannerQueryError =
	| GetLockoutBanner400
	| void
	| GetLockoutBanner403
	| GetLockoutBanner500;

export function useGetLockoutBanner<
	TData = Awaited<ReturnType<typeof getLockoutBanner>>,
	TError =
		| GetLockoutBanner400
		| void
		| GetLockoutBanner403
		| GetLockoutBanner500,
>(
	organizationVerdiId: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getLockoutBanner>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getLockoutBanner>>,
					TError,
					Awaited<ReturnType<typeof getLockoutBanner>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLockoutBanner<
	TData = Awaited<ReturnType<typeof getLockoutBanner>>,
	TError =
		| GetLockoutBanner400
		| void
		| GetLockoutBanner403
		| GetLockoutBanner500,
>(
	organizationVerdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getLockoutBanner>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getLockoutBanner>>,
					TError,
					Awaited<ReturnType<typeof getLockoutBanner>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLockoutBanner<
	TData = Awaited<ReturnType<typeof getLockoutBanner>>,
	TError =
		| GetLockoutBanner400
		| void
		| GetLockoutBanner403
		| GetLockoutBanner500,
>(
	organizationVerdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getLockoutBanner>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get an organization's lockout banner
 */

export function useGetLockoutBanner<
	TData = Awaited<ReturnType<typeof getLockoutBanner>>,
	TError =
		| GetLockoutBanner400
		| void
		| GetLockoutBanner403
		| GetLockoutBanner500,
>(
	organizationVerdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getLockoutBanner>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetLockoutBannerQueryOptions(
		organizationVerdiId,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get an organization's lockout banner
 */
export const useSetGetLockoutBannerQueryData = () => {
	const queryClient = useQueryClient();
	return (
		organizationVerdiId: string,
		updater:
			| Awaited<ReturnType<typeof getLockoutBanner>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getLockoutBanner>> | undefined,
			  ) => Awaited<ReturnType<typeof getLockoutBanner>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getLockoutBanner>>>(
			{
				exact: $exactMatch,
				queryKey: getGetLockoutBannerQueryKey(organizationVerdiId),
			},
			updater,
		);
	};
};

/**
 * @summary Get an organization's lockout banner
 */
export const useGetGetLockoutBannerQueryData = () => {
	const queryClient = useQueryClient();
	return (organizationVerdiId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getLockoutBanner>>>(
			getGetLockoutBannerQueryKey(organizationVerdiId),
		);
};

export const getPutLockoutBannerUrl = (organizationVerdiId: string) => {
	return `/lockout-banners/${organizationVerdiId}`;
};

/**
 * Send the current `versionId` when replacing a banner; a missing or stale one is refused with 409.
 *
 * **Access:** Requires a user session token. Administrators only.
 * @summary Create or replace an organization's lockout banner
 */
export const putLockoutBanner = async (
	organizationVerdiId: string,
	lockoutBannersCreate: LockoutBannersCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<LockoutBanners> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<LockoutBanners>(
		getPutLockoutBannerUrl(organizationVerdiId),
		{
			...options,
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				...getHeaders(options?.headers),
			},
			body: JSON.stringify(lockoutBannersCreate),
		},
	);
};

export const getPutLockoutBannerMutationKey = () =>
	["putLockoutBanner"] as const;

export const getPutLockoutBannerMutationOptions = <
	TError =
		| PutLockoutBanner400
		| void
		| PutLockoutBanner404
		| PutLockoutBanner409
		| PutLockoutBanner500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof putLockoutBanner>>,
		TError,
		PutLockoutBannerMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof putLockoutBanner>>,
	TError,
	PutLockoutBannerMutationVariables,
	TContext
> => {
	const mutationKey = getPutLockoutBannerMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof putLockoutBanner>>,
		PutLockoutBannerMutationVariables
	> = (props) => {
		const { organizationVerdiId, data } = props ?? {};

		return putLockoutBanner(organizationVerdiId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type PutLockoutBannerMutationResult = NonNullable<
	Awaited<ReturnType<typeof putLockoutBanner>>
>;
export type PutLockoutBannerMutationBody = LockoutBannersCreate;
export type PutLockoutBannerMutationError =
	| PutLockoutBanner400
	| void
	| PutLockoutBanner404
	| PutLockoutBanner409
	| PutLockoutBanner500;
export type PutLockoutBannerMutationVariables = {
	organizationVerdiId: string;
	data: LockoutBannersCreate;
};

/**
 * @summary Create or replace an organization's lockout banner
 */
export const usePutLockoutBanner = <
	TError =
		| PutLockoutBanner400
		| void
		| PutLockoutBanner404
		| PutLockoutBanner409
		| PutLockoutBanner500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof putLockoutBanner>>,
			TError,
			PutLockoutBannerMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof putLockoutBanner>>,
	TError,
	PutLockoutBannerMutationVariables,
	TContext
> => {
	return useMutation(getPutLockoutBannerMutationOptions(options), queryClient);
};
